import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { ProposalStatusBadge } from "../ProposalStatusBadge";
import {
  ProposalRecipient,
  ProposalRecipients,
  ProposalStatus
} from "../types";
import { RECIPIENT_ROLES, recipientsWithRole } from "./recipients";
import { formatDateTimeToMinute } from "@/utils/datetime";

interface Props {
  recipients: ProposalRecipients;
}

// Opened, for a person, reads as Viewed; a decline shows when and why.
const RecipientStatus: React.FC<{ recipient: ProposalRecipient }> = ({
  recipient
}) => {
  const { t } = useTranslation();
  if (!recipient.status) return null;
  const badge = (
    <ProposalStatusBadge
      proposal={{ status: recipient.status }}
      label={
        recipient.status === ProposalStatus.DELIVERED
          ? t("contracts.proposals.recipients.viewed")
          : undefined
      }
    />
  );
  if (recipient.status !== ProposalStatus.DECLINED || !recipient.declinedAt) {
    return badge;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} data-test="proposal-recipient-declined">
          {badge}
        </span>
      </TooltipTrigger>
      {/* The reason is what the signer typed: plain text, never markdown. */}
      <TooltipContent enableMarkdown={false} maxWidth={20}>
        <div className="flex flex-col gap-1">
          <Text variant="smBodySecondary">
            {t("contracts.proposals.recipients.declinedAt", {
              at: formatDateTimeToMinute(recipient.declinedAt)
            })}
          </Text>
          {recipient.declineReason ? (
            <Text variant="smBody" className="whitespace-pre-wrap break-words">
              {recipient.declineReason}
            </Text>
          ) : null}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

// Who a proposal went to, and where each of their requests stands.
export const RecipientStatusList: React.FC<Props> = ({ recipients }) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-col gap-4 p-4 rounded-2xl border border-border-widget-card bg-surface-widget-card"
      data-test="proposal-recipient-statuses"
    >
      <Text variant="smSubtitle">
        {t("contracts.proposals.editor.recipients.title")}
      </Text>
      {recipients.list.length === 0 ? (
        <Text variant="bodyTertiary" className="px-1">
          {t("contracts.proposals.editor.recipients.empty")}
        </Text>
      ) : (
        RECIPIENT_ROLES.map((role) => {
          const group = recipientsWithRole(recipients, role);
          if (group.length === 0) return null;
          return (
            <div
              key={role}
              className="flex flex-col gap-1"
              data-test={`proposal-recipient-statuses-${role}`}
            >
              <Text variant="smBodySecondary" className="px-1">
                {t(`contracts.proposals.recipients.role.${role}`)}
              </Text>
              <ul className="flex flex-col">
                {group.map((recipient) => (
                  <li
                    key={recipient.key}
                    className="flex items-center justify-between gap-2 h-10 px-1"
                    data-test={`proposal-recipient-status-${recipient.key}`}
                  >
                    <Text
                      variant="body"
                      className="truncate"
                      hasTruncatedTooltip
                    >
                      {recipient.name}
                    </Text>
                    <RecipientStatus recipient={recipient} />
                  </li>
                ))}
              </ul>
            </div>
          );
        })
      )}
    </div>
  );
};
